import {
  Button,
  Divider,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Text,
} from "@chakra-ui/react";
import { useAdjustmentsTable } from "features/capital-activity/event/adjustments/useAdjustmentsTable";
import { GenericTableData } from "gen/model/genericTableData";
import { useCallback, useRef } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Form from "shared/components/form/Form";
import { NavConfirmationConfirmDialog } from "shared/components/form/FormNavConfirmationDialog";
import { useConfirmationDialog } from "shared/components/modal/confirm/ConfirmationDialog";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import {
  DataTable,
  DataTableRef,
} from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { suppressAutoSizePinnedColumns } from "shared/components/table/data/DataTableUtils";
import { useToast } from "shared/components/toast/useToast";
import { ModalCancelButton } from "shared/modal/ModalCancelButton";
import { flattenApiErrorData, getApiError } from "shared/utils/ErrorUtils";

export const CapitalActivityAdjustmentsModal = <
  FormValues extends FieldValues,
>({
  isOpen,
  onClose,
  isLoading,
  onSubmit,
  tableData,
}: {
  tableData: GenericTableData;
  isLoading: boolean;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formValues: FormValues) => Promise<void>;
}) => {
  const { t } = useTranslation(["common", "capitalActivity"]);
  const { showErrorToast } = useToast();

  const confirmCancel = useConfirmationDialog(NavConfirmationConfirmDialog);

  const form = useForm<FormValues>();
  const { isDirty, isSubmitting } = form.formState;

  const onModalClose = useCallback(async () => {
    if (isDirty) {
      const confirmResult = await confirmCancel();
      if (confirmResult === "submit") {
        await onSubmit(form.getValues());
        onClose();
      } else if (confirmResult === "discard") {
        onClose();
      }
    } else {
      onClose();
    }
  }, [isDirty, confirmCancel, onSubmit, form, onClose]);

  const ref = useRef<DataTableRef>(null);
  const { renderCellValue, onClear, stylesForCell } = useAdjustmentsTable(ref);

  return (
    <Modal
      isOpen={isOpen}
      size="full"
      scrollBehavior="inside"
      onClose={onModalClose}
    >
      <Form
        successMessage={t("capitalActivity:adjustments.success")}
        onSuccess={onClose}
        onError={(e) => {
          // TODO better handling
          const rootListError = flattenApiErrorData(getApiError(e)?.data).find(
            (error) => error.fieldPath === "adjustments",
          );
          if (rootListError) {
            showErrorToast(rootListError.error);
          } else {
            showErrorToast(t("capitalActivity:adjustments.error"));
          }
          return true;
        }}
        form={form}
        onSubmit={onSubmit}
      >
        <ModalContent backgroundColor="gray.50">
          <ModalHeader pt="16px" pb="8px">
            <Text textStyle="helper">
              {t("capitalActivity:adjustments.helperText")}
            </Text>
          </ModalHeader>
          <Divider />
          <ModalBody display="flex" flexDirection="column">
            <TableBox
              isFullHeight={true}
              isDefaultExpanded={true}
              isFillHeight={true}
              isFullWidth={true}
              boxProps={{
                sx: {
                  borderTopWidth: "none",
                },
              }}
            >
              <TableBoxHeader>
                <TableBoxTitle>
                  {t("capitalActivity:adjustments.title")}
                </TableBoxTitle>
                <DataTableBoxActions
                  showColControl={false}
                  showFullScreen={false}
                  showClearConfig={{
                    display: true,
                    onClear,
                  }}
                />
              </TableBoxHeader>
              <TableBoxBody>
                <DataTable
                  suppressStatusBar={true}
                  renderCellValue={renderCellValue}
                  ref={ref}
                  data={tableData}
                  isLoading={isLoading}
                  stylesForCell={stylesForCell}
                  getColumnDef={suppressAutoSizePinnedColumns}
                />
              </TableBoxBody>
            </TableBox>
          </ModalBody>
          <ModalFooter
            background="white"
            borderTopWidth="1px"
            gap="8px"
            py="12px"
            px="24px"
          >
            <ModalCancelButton />
            <Button isLoading={isSubmitting} type="submit">
              {t("capitalActivity:adjustments.save")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Form>
    </Modal>
  );
};
